import React, { createContext, useCallback, useEffect, useMemo, useRef, useState, useContext } from 'react'
import { useSelector } from 'react-redux'
import { useBrandContext } from '@vp/react-brand'
import { LOCAL_STORAGE_KEYS } from '~/client/constants'
import { getGalleryIdSelector } from '~/client/store/config'
import { usePersonalizationConfig } from './hooks/usePersonalizationConfig'
import { usePersonalizationModalState } from './hooks/usePersonalizationModalState'
import { useSchemesStore } from '../Schemes'
import { Translations, usePersonalizationTranslations } from './hooks/usePersonalizationTranslations'
import { PersonalizationAnalytics, usePersonalizationAnalytics } from './hooks/usePersonalizationAnalytics'

export enum SCHEME_KEYS {
  MANUAL = 'MANUAL',
  BRAND = 'BRAND',
}

interface PersonalizationManagerContextType {
  isInitialized: boolean
  mobileModalOpen: boolean
  setMobileModalOpen: (isOpen: boolean) => void
  uploadModalOpen: boolean
  setUploadModalOpen: (isOpen: boolean) => void
  translations: Translations
  tracking: PersonalizationAnalytics
}

const PersonalizationManagerContext = createContext<PersonalizationManagerContextType | undefined>(undefined)

export const PersonalizationManager: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isEnabled, imageCopyKey } = usePersonalizationConfig()
  const [isInitialized, setIsInitialized] = useState<boolean>(false)
  const [localStorageActiveSchemeId, setLocalStorageActiveSchemeId] = useState<string | undefined>(undefined)
  const galleryId = useSelector(getGalleryIdSelector)
  const brandContext = useBrandContext()
  const translations = usePersonalizationTranslations(imageCopyKey)
  const tracking = usePersonalizationAnalytics()
  const { mobileModalOpen, setMobileModalOpen, uploadModalOpen, setUploadModalOpen } = usePersonalizationModalState(isInitialized)
  const { loadScheme, setActiveScheme } = useSchemesStore()

  useEffect(() => {
    const storedActiveSchemeId = localStorage.getItem(LOCAL_STORAGE_KEYS.V2_ACTIVE_SESSION + galleryId)

    if (storedActiveSchemeId) {
      setLocalStorageActiveSchemeId(storedActiveSchemeId)
    }
  }, [galleryId])

  const hasInitializedRef = useRef(false) // used to block reruns

  const restorePersistedSchemes = useCallback(
    async (schemeIds: string[]): Promise<void> => {
      const getSchemeId = (key: string): string | null => localStorage.getItem(LOCAL_STORAGE_KEYS.V2_PERSISTED_SESSION + key + galleryId)

      const restorePromises = schemeIds.map(async (key) => {
        const sessionId = getSchemeId(key)

        if (!sessionId) {
          return
        }

        try {
          await loadScheme({ schemeId: key, sessionId })
          if (localStorageActiveSchemeId === key) {
            setActiveScheme(key)
          }
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (error) {
          localStorage.removeItem(LOCAL_STORAGE_KEYS.V2_PERSISTED_SESSION + key + galleryId)
        }
      })

      await Promise.all(restorePromises)
    },
    [localStorageActiveSchemeId, galleryId, loadScheme, setActiveScheme],
  )

  useEffect(() => {
    const initialize = async (): Promise<void> => {
      if (hasInitializedRef.current || !brandContext.isInitialized || !isEnabled) {
        return
      }
      hasInitializedRef.current = true // prevent reruns

      const initPromises: Promise<void>[] = [restorePersistedSchemes([SCHEME_KEYS.MANUAL])]

      await Promise.all(initPromises)

      setIsInitialized(true)
    }

    initialize()
  }, [brandContext.activeBrand, brandContext.brands, brandContext.isInitialized, galleryId, isInitialized, isEnabled, restorePersistedSchemes, setActiveScheme])

  const contextValue = useMemo(
    () => ({
      isInitialized,
      tracking,
      mobileModalOpen,
      setMobileModalOpen,
      uploadModalOpen,
      setUploadModalOpen,
      translations,
    }),
    [isInitialized, tracking, mobileModalOpen, setMobileModalOpen, setUploadModalOpen, uploadModalOpen, translations],
  )

  return <PersonalizationManagerContext.Provider value={contextValue}>{children}</PersonalizationManagerContext.Provider>
}

export const usePersonalizationManager = (): PersonalizationManagerContextType => {
  const context = useContext(PersonalizationManagerContext)

  if (!context) {
    throw new Error('usePersonalizationManager must be used within a PersonalizationManager')
  }
  return context
}
